import React from "react";
import { Box, Typography, Grid, Accordion, AccordionSummary, AccordionDetails, TextField } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import { ExpandMore } from "@mui/icons-material";
import { Alert } from "@mui/material";
import { PrimaryButton, SecondaryButton } from "../../../../layout/Button/Button";
import { useBranchedStoryEdit } from "../../contexts/BranchedStoryEditContext";
import { VCard } from "../../../../../core/components/VCard";

const useStyles = makeStyles(() =>
    createStyles({
        formContainer: {
            padding: "24px",
        },
        accordion: {
            padding: "0 16px 0 16px",
            marginBottom: "16px!important",
            backgroundColor: "rgba(0,0,0,0.05)",
            width: "100%",
        },
        accordionHeader: {
            minHeight: "64px",
        },
        partNumber: {
            backgroundColor: "#121738",
            color: "#ffffff",
            borderRadius: "50%",
            width: "32px",
            height: "32px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            fontWeight: "bold",
            marginRight: "12px",
        },
        actionButtons: {
            marginTop: "32px",
            display: "flex",
            gap: "16px",
            justifyContent: "flex-end",
        },
    }),
);

interface EditStoryPartsProps {
    onComplete: () => void;
}

export const EditStoryParts: React.FC<EditStoryPartsProps> = ({ onComplete }) => {
    const classes = useStyles();
    const { currentStory, updateStoryPart, editedParts, hasPartChanges, addStoryPart } = useBranchedStoryEdit();

    const handlePartContentChange = (partId: number, newContent: string) => {
        updateStoryPart(partId, { content: newContent });
    };

    const handlePartLabelChange = (partId: number, newLabel: string) => {
        updateStoryPart(partId, { partLabel: newLabel });
    };

    // Get the current content for a part (from edited state if available, otherwise from original)
    const getPartContent = (part: { id: number; content?: string }) => {
        const editedPart = editedParts.get(part.id);
        return editedPart?.content !== undefined ? editedPart.content : part.content || "";
    };

    // Get the current label for a part (from edited state if available, otherwise from original)
    const getPartLabel = (part: { id: number; partLabel?: string }) => {
        const editedPart = editedParts.get(part.id);
        return editedPart?.partLabel !== undefined ? editedPart.partLabel : part.partLabel || "";
    };

    return (
        <VCard>
            <Box className={classes.formContainer}>
                <Typography variant="h4" gutterBottom>
                    Edit Story Parts
                </Typography>

                {/* Changes tracking notification */}
                {hasPartChanges && (
                    <Alert severity="info" style={{ marginBottom: 16 }}>
                        <strong>Changes detected!</strong> Your story part edits are being tracked. Continue to the
                        Review step to save all changes.
                    </Alert>
                )}

                {/* Display editable story parts */}
                {currentStory.StoryParts && currentStory.StoryParts.length > 0 && (
                    <Box mt={3}>
                        <Typography variant="h6" gutterBottom>
                            Story Parts ({currentStory.StoryParts.length} parts)
                        </Typography>
                        {currentStory.StoryParts.map((part) => (
                            <Accordion
                                key={part.id}
                                className={classes.accordion}
                                elevation={0}
                                defaultExpanded={false}
                                style={{ padding: "16px" }}
                            >
                                <AccordionSummary
                                    style={{ padding: "0px" }}
                                    expandIcon={<ExpandMore />}
                                    className={classes.accordionHeader}
                                >
                                    <Box display="flex" alignItems="center">
                                        <span className={classes.partNumber}>{part.part}</span>
                                        <Typography variant="h6">Story Part {part.part}</Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails style={{ height: "100%", padding: "0px" }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Part Label (Optional)"
                                                value={getPartLabel(part)}
                                                fullWidth
                                                variant="outlined"
                                                onChange={(e) => handlePartLabelChange(part.id, e.target.value)}
                                                style={{ marginBottom: "16px" }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Content"
                                                value={getPartContent(part)}
                                                fullWidth
                                                multiline
                                                variant="outlined"
                                                onChange={(e) => handlePartContentChange(part.id, e.target.value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                        ))}

                        {/* Add Story Part button (max 7 parts) */}
                        {currentStory.StoryParts && currentStory.StoryParts.length < 7 && (
                            <Box mt={2} display="flex" justifyContent="center">
                                <SecondaryButton onClick={addStoryPart}>+ Add Story Part</SecondaryButton>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Action Buttons */}
                <Box className={classes.actionButtons}>
                    <SecondaryButton type="button">Skip for Now</SecondaryButton>
                    <PrimaryButton onClick={onComplete}>Continue to Decision Points</PrimaryButton>
                </Box>
            </Box>
        </VCard>
    );
};

// Display name for debugging (following design guide)
EditStoryParts.displayName = "EditStoryParts";
